// hooks/useSessionForm.js - VERSION PRESTATIONS
import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import logger from '../utils/logger';
import { API_ENDPOINTS } from '../utils/apiConfig';
import { toLocalDateStr } from '../utils/dateUtils';

/**
 * Hook principal pour la gestion du formulaire de prestation
 * Migration v2.0: sessions -> prestations, rooms -> groups, trainers -> team_leaders
 */
export const useSessionForm = (token, editingSession, showError, showInfo) => {
  // Alias pour compatibilite
  const editingPrestation = editingSession;

  const hookId = useRef(null);
  if (!hookId.current) {
    hookId.current = Math.random().toString(36).substr(2, 9);
    logger.debug('useSessionForm - Initialisation UNIQUE du hook', hookId.current);
  }

  const isInitialized = useRef(false);
  const formInitialized = useRef(false);
  const loadingRef = useRef(false);
  const dataLoadedRef = useRef(false);

  const stableToken = useRef(token);
  const stableEditingPrestation = useRef(editingPrestation);

  if (stableToken.current !== token) {
    logger.debug('Token change:', stableToken.current, '->', token);
    stableToken.current = token;
    isInitialized.current = false;
    dataLoadedRef.current = false;
  }

  if (JSON.stringify(stableEditingPrestation.current) !== JSON.stringify(editingPrestation)) {
    logger.debug('EditingPrestation change');
    stableEditingPrestation.current = editingPrestation;
    formInitialized.current = false;
  }

  // Etats principaux
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('basic');

  // Etats de reference - Nouveau nommage
  const [groups, setGroups] = useState([]);
  const [clients, setClients] = useState([]);
  const [locations, setLocations] = useState([]);
  const [teamLeaders, setTeamLeaders] = useState([]);
  const [users, setUsers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [statuses, setStatuses] = useState([]);

  // Alias pour compatibilite
  const rooms = groups;
  const setRooms = setGroups;
  const trainers = clients;
  const setTrainers = setClients;

  // Gestion des conflits
  const [conflicts, setConflicts] = useState([]);

  // Fonction de formatage de date
  const formatDateForInput = useCallback((dateString) => {
    if (!dateString) return '';

    if (dateString.includes('T')) {
      const utcDate = new Date(dateString);
      const year = utcDate.getUTCFullYear();
      const month = String(utcDate.getUTCMonth() + 1).padStart(2, '0');
      const day = String(utcDate.getUTCDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }

    if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
      return dateString;
    }

    return dateString;
  }, []);

  // Fonction d'initialisation des donnees de formulaire
  const getInitialFormData = useCallback(() => {
    const today = toLocalDateStr(new Date());

    if (stableEditingPrestation.current) {
      const p = stableEditingPrestation.current;
      return {
        // ID pour détecter le mode édition
        id: p.id,
        // Nouveau nommage
        prestation_type: p.prestation_type || p.session_type || 'prestation',
        group_id: p.group_id || p.room_id || '',
        client_id: p.client_id || '',
        // Utiliser location_value (calculé par le backend) : soit location_id, soit "client_XX"
        location_id: p.location_value || (p.location_client_id ? `client_${p.location_client_id}` : (p.location_id || '')),
        team_leader_id: p.team_leader_id || p.trainer_id || '',
        status_id: p.status_id || '',
        start_date: formatDateForInput(p.start_date) || today,
        end_date: formatDateForInput(p.end_date) || today,
        departure_time: p.departure_time || '',
        end_time: p.end_time || '',
        production_hours: String(p.production_hours || p.training_hours || 7),
        travel_hours: String(p.travel_hours || 1),
        needs_lunch: p.needs_lunch || false,
        needs_bag: p.needs_bag || false,
        accommodation_type: p.accommodation_type || 'none',
        participant_ids: (() => {
          const ids = p.participants ? p.participants.map(part => part.user_id) : [];
          // ✅ S'assurer que le chef d'équipe est dans les participants
          const leaderId = p.team_leader_id || p.trainer_id;
          if (leaderId && !ids.includes(leaderId)) {
            ids.push(leaderId);
          }
          return ids;
        })(),
        block_reason: p.block_reason || '',
        maintenance_description: p.maintenance_description || '',
        // Nouveaux champs prestations
        order_number: p.order_number || '',
        work_type: p.work_type || '',
        objective: p.objective || '',
        // Alias pour compatibilite
        session_type: p.prestation_type || p.session_type || 'prestation',
        room_id: p.group_id || p.room_id || '',
        trainer_id: p.team_leader_id || p.trainer_id || '',
        training_hours: String(p.production_hours || p.training_hours || 7)
      };
    } else {
      return {
        // Nouveau nommage
        prestation_type: 'prestation',
        group_id: '',
        client_id: '',
        location_id: '',
        team_leader_id: '',
        status_id: '',
        start_date: today,
        end_date: today,
        departure_time: '',
        end_time: '',
        production_hours: '7',
        travel_hours: '1',
        needs_lunch: false,
        needs_bag: false,
        accommodation_type: 'none',
        participant_ids: [],
        block_reason: '',
        maintenance_description: '',
        // Nouveaux champs prestations
        order_number: '',
        work_type: '',
        objective: '',
        // Alias pour compatibilite
        session_type: 'prestation',
        room_id: '',
        trainer_id: '',
        training_hours: '7'
      };
    }
  }, [formatDateForInput]);

  // Initialisation : Etat du formulaire SEULEMENT une fois
  const [formData, setFormData] = useState(() => {
    const initial = getInitialFormData();
    logger.debug('Initialisation formData UNIQUE:', initial);
    return initial;
  });

  // Chargement des donnees - Version prestations
  useEffect(() => {
    const loadData = async () => {
      if (!stableToken.current) {
        logger.debug('Pas de token disponible dans useSessionForm');
        return;
      }

      if (dataLoadedRef.current) {
        logger.debug('Donnees deja chargees, skip');
        return;
      }

      if (loadingRef.current) {
        logger.debug('Chargement deja en cours, skip');
        return;
      }

      logger.debug('=== CHARGEMENT DONNEES useSessionForm ===');
      logger.debug('Hook ID:', hookId.current);

      dataLoadedRef.current = true;
      loadingRef.current = true;
      setLoading(true);
      setError('');

      try {
        const headers = {
          'Authorization': `Bearer ${stableToken.current}`,
          'Content-Type': 'application/json'
        };

        // Utiliser le batch endpoint pour charger toutes les données en un seul appel
        logger.debug('Chargement données via batch API...');
        const batchResponse = await fetch(API_ENDPOINTS.BATCH_SESSION_FORM, { headers });

        if (batchResponse.ok) {
          const batchData = await batchResponse.json();

          if (batchData.success) {
            const { groups: groupsList, clients: clientsList, locations: locationsList, users: allUsers, statuses: statusesList, teamLeaders: leadersList } = batchData.data;

            // Groups
            setGroups(groupsList || []);
            // Teams = Groups de type "prestation" uniquement, triés par nom
            const prestationGroups = (groupsList || [])
              .filter(g => !g.group_type || g.group_type === 'prestation')
              .sort((a, b) => (a.name || '').localeCompare(b.name || '', 'fr'));
            setTeams(prestationGroups);
            logger.debug('Groups charges:', groupsList?.length || 0, '- Teams (prestation):', prestationGroups.length);

            // Clients
            setClients(clientsList || []);
            logger.debug('Clients charges:', clientsList?.length || 0);

            // Locations
            setLocations(locationsList || []);
            logger.debug('Locations chargees:', locationsList?.length || 0);

            // Users - Garder tous les utilisateurs pour pouvoir afficher les participants existants
            // Le filtrage pour l'ajout de nouveaux participants se fait dans ParticipantsInterface
            setUsers(allUsers || []);
            logger.debug('Users charges:', allUsers?.length || 0);

            // Team leaders (déjà filtrés par le backend, mais on refait au cas où)
            setTeamLeaders(leadersList || []);
            logger.debug('Team leaders:', leadersList?.length || 0);

            // Statuses
            setStatuses(statusesList || []);
            logger.debug('Statuses charges:', statusesList?.length || 0);

            logger.debug('BATCH CHARGEMENT TERMINE avec succes', {
              duration_ms: batchData.meta?.duration_ms
            });
          }
        } else {
          throw new Error(`Erreur batch API: ${batchResponse.status}`);
        }

        if (showInfo && typeof showInfo === 'function') {
          showInfo('Donnees chargees', {
            title: 'Pret',
            duration: 1000
          });
        }

      } catch (err) {
        logger.error('Erreur chargement useSessionForm:', err);
        setError(err.message);
        dataLoadedRef.current = false;

        if (showError && typeof showError === 'function') {
          showError('Erreur chargement', {
            title: 'Echec',
            details: err.message
          });
        }
      } finally {
        setLoading(false);
        loadingRef.current = false;
      }
    };

    if (stableToken.current && !dataLoadedRef.current && !loadingRef.current) {
      loadData();
    }
  }, []);

  // Detection multi-jours
  const multiDayInfo = useMemo(() => {
    if (!formData.start_date || !formData.end_date) {
      return { days: [], totalDays: 0 };
    }

    const start = new Date(formData.start_date);
    const end = new Date(formData.end_date);
    const isSameDay = start.getTime() === end.getTime();

    if (isSameDay) {
      return {
        days: [{ date: formData.start_date, label: 'Jour unique' }],
        totalDays: 1
      };
    }

    const days = [];
    const currentDate = new Date(start);

    while (currentDate <= end) {
      const dayOfWeek = currentDate.getDay();
      if (dayOfWeek >= 1 && dayOfWeek <= 5) {
        const dateStr = toLocalDateStr(currentDate);
        const dayNumber = days.length + 1;
        const dayLabel = `Jour ${dayNumber} - ${currentDate.toLocaleDateString('fr-FR', {
          weekday: 'short',
          day: '2-digit',
          month: '2-digit'
        })}`;

        days.push({ date: dateStr, label: dayLabel, dayNumber });
      }
      currentDate.setDate(currentDate.getDate() + 1);
    }

    return {
      days,
      totalDays: days.length
    };
  }, [formData.start_date, formData.end_date]);

  // Gestionnaire de changement de champ
  const handleChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : value;

    setFormData(prevData => {
      if (prevData[name] === newValue) {
        return prevData;
      }

      const newData = { ...prevData, [name]: newValue };

      // Reset du statut si changement de type
      if (name === 'prestation_type' && newValue !== prevData.prestation_type) {
        newData.status_id = '';
      }
      // Compatibilite avec l'ancien nom
      if (name === 'session_type' && newValue !== prevData.session_type) {
        newData.status_id = '';
        newData.prestation_type = newValue;
      }

      // Synchroniser les alias
      if (name === 'group_id') newData.room_id = newValue;
      if (name === 'room_id') newData.group_id = newValue;
      if (name === 'team_leader_id') newData.trainer_id = newValue;
      if (name === 'trainer_id') newData.team_leader_id = newValue;
      if (name === 'production_hours') newData.training_hours = newValue;
      if (name === 'training_hours') newData.production_hours = newValue;
      if (name === 'prestation_type') newData.session_type = newValue;
      if (name === 'session_type') newData.prestation_type = newValue;

      // ✅ Ajouter automatiquement le chef d'équipe aux participants
      if ((name === 'team_leader_id' || name === 'trainer_id') && newValue) {
        const currentParticipants = newData.participant_ids || [];
        if (!currentParticipants.includes(newValue)) {
          newData.participant_ids = [...currentParticipants, newValue];
          logger.debug('Chef d\'équipe ajouté aux participants:', newValue);
        }
      }

      return newData;
    });
  }, []);

  // Types de prestation
  const prestationTypes = useMemo(() => [
    { value: 'prestation', label: 'Prestation', description: 'Prestation avec participants', icon: 'P' },
    { value: 'blocked', label: 'Blocage', description: 'Prestation en attente de confirmation', icon: 'B' },
    { value: 'note', label: 'Note', description: 'Information ou maintenance', icon: 'N' }
  ], []);

  // Alias pour compatibilite
  const sessionTypes = prestationTypes;

  // Onglets dynamiques
  const tabs = useMemo(() => {
    const baseTabs = [
      { id: 'basic', label: 'Informations', icon: 'i' },
      { id: 'participants', label: 'Participants', icon: 'p' },
      { id: 'options', label: 'Options', icon: 'o' }
    ];

    return baseTabs;
  }, []);

  // Fonction de reset
  const resetForm = useCallback(() => {
    logger.debug('Reset useSessionForm');
    setFormData(getInitialFormData());
    setError('');
    setConflicts([]);
  }, [getInitialFormData]);

  // RETURN STABLE
  return {
    // Etats
    formData,
    setFormData,
    loading,
    setLoading,
    error,
    setError,
    activeTab,
    setActiveTab,
    conflicts,
    setConflicts,

    // Donnees de reference - Nouveau nommage
    groups,
    setGroups,
    clients,
    setClients,
    locations,
    setLocations,
    teamLeaders,
    setTeamLeaders,
    users,
    setUsers,
    teams,
    setTeams,
    statuses,
    setStatuses,

    // Alias pour compatibilite
    rooms,
    setRooms,
    trainers,
    setTrainers,

    // Informations calculees
    multiDayInfo,
    prestationTypes,
    sessionTypes,
    tabs,

    // Fonctions
    handleChange,
    formatDateForInput,
    resetForm,

    // Refs
    isInitialized,
    formInitialized,

    // Prestation info
    editingPrestation: stableEditingPrestation.current,
    editingSession: stableEditingPrestation.current
  };
};

export default useSessionForm;
